import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { useAuth } from '../AuthContext';

const VideoUploader = ({ uploadedVideos, setUploadedVideos, removeUploadedVideos, maxDuration }) => {
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const { jwtToken: token } = useAuth();
  const MAX_FILE_SIZE_MB = 880;

  // Helper to read video duration via a Promise
  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = () => resolve(null);
      video.src = URL.createObjectURL(file);
    });
  };

  const uploadVideo = async (file) => {
    setUploading(true);
    setMessage('');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('video', file);

    try {
      const response = await axios.post(
        'https://api.schedpilot.com/wp-json/custom/v1/upload-video',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          },
        }
      );

      if (response.data.code === 200) {
        setMessage('Upload successful!');
        setUploadedVideos((prev) => [
          ...prev,
          { url: response.data.file_url, id: response.data.video_id },
        ]);
      } else {
        setMessage('Upload failed.');
      }
    } catch (error) {
      console.error(error);
      setMessage('Error uploading video.');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    // Use a for...of loop to handle async validation one by one
    for (const file of acceptedFiles) {
      // 1. Check File Size
      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > MAX_FILE_SIZE_MB) {
        setMessage(`File "${file.name}" exceeds the 880MB limit.`);
        continue;
      }

      // 2. Check Duration (TikTok requirement)
      if (maxDuration) {
        const duration = await getVideoDuration(file);
        if (duration && duration > maxDuration) {
          setMessage(`"${file.name}" is ${Math.round(duration)}s. TikTok limit is ${maxDuration}s.`);
          continue; 
        }
      }

      // 3. If all checks pass, upload
      await uploadVideo(file);
    }
  }, [token, maxDuration, setUploadedVideos]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: { 'video/*': [] },
  });

  const handleDeleteVideo = async (urlToDelete) => {
    try {
      const response = await axios.post(
        'https://api.schedpilot.com/wp-json/custom/v1/delete-video',
        { file_url: urlToDelete },
        { withCredentials: true }
      );

      if (response.data.code === 200) {
        setMessage('Video deleted.');
        setUploadedVideos((prev) => prev.filter((vid) => vid.url !== urlToDelete));
      } else {
        setMessage('Failed to delete video.');
      }
    } catch (error) {
      console.error(error);
      setMessage('Error deleting video.');
    }
  };

  return (
    <div style={{ border: '2px dashed #ccc', padding: 20, borderRadius: 8, backgroundColor: '#fff', marginTop: 20 }}>
      <div {...getRootProps()} style={{ cursor: 'pointer', padding: 30, textAlign: 'center' }}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the video(s) here ...</p>
        ) : (
          <p>Drag & drop video(s) here, or click to select files</p>
        )}
      </div>

      {uploading && (
        <div className="mt-2">
          <p>Uploading... {uploadProgress}%</p>
          <div style={{ height: 10, background: '#e0e0e0', borderRadius: 5 }}>
            <div
              style={{
                width: `${uploadProgress}%`,
                background: '#007bff',
                height: '100%',
                borderRadius: 5,
                transition: 'width 0.2s',
              }}
            />
          </div>
        </div>
      )}

      {message && <p className={message.includes('successful') ? "text-success mt-2" : "text-danger mt-2"}>{message}</p>}

      {uploadedVideos.length > 0 && (
        <div className="mt-3 d-flex flex-wrap" style={{ backgroundColor: '#f2f2f2', padding: 10 }}>
          {uploadedVideos.map((vid, index) => (
            <div className="d-flex flex-column mr-3 mb-3" key={index}>
              <video
                src={vid.url}
                controls
                className="img-thumbnail"
                style={{ width: '150px', height: '100px', objectFit: 'cover' }}
              />
              <button
                className="btn btn-sm btn-danger mt-2"
                style={{ borderRadius: 5 }}
                onClick={() => removeUploadedVideos(index)}
                disabled={uploading}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoUploader;